import {
  AGENT_DATA_BASIS_KINDS,
  SAMPLE_PER_DAY_MAX,
  SAMPLE_PER_RESPONSE_MAX,
} from '@tabel/shared/schemas/agent-data';
import { useMutation } from '@tanstack/react-query';
import { ShieldOff, TriangleAlert } from 'lucide-react';
import { type FormEvent, type ReactNode, type RefObject, useId, useRef, useState } from 'react';
import { ErrorCodes, errorCode, errorSentence, errorText, useToast } from '../../components/ErrorToast';
import { AlertDialog, Button, Card, CardHeader, inputCls } from '../../components/ui';
import { ApiError, reportClientError } from '../../lib/errors';
import { type AgentDataClient, type AgentDataPut, type AgentDataView, saveAgentData } from './api';
import { SCREEN, shouldReport } from './parts';
import { BASIS_KIND_LABEL, LABEL, T, TITLE } from './texts';

/*
 * Разрешение на выборки дней (005, T139; FR-028; contracts/http-api.md, `PUT /api/admin/agent-data`): форма с кнопкой
 * «Сохранить изменения» — включено или нет, основание (вид, номер, дата), клиенты, лимиты. Тело — `AgentDataPut`.
 * - Проверка — при отправке: включить без основания нельзя (ошибки у полей, фокус в первое поле с ошибкой); ошибка
 *   исчезает, как только значение стало верным. Лимиты — целые числа в пределах общей схемы: в ответе от 1 до 20,
 *   в сутки — от 1 до 10 000.
 * - Клиенты — флажки белого списка входа OAuth (`available`) и клиенты разрешения, которых в нём уже нет (их можно
 *   снять). Имена и хосты — с сервера.
 * - Выключение отзывает ссылки выборок: перед записью — окно подтверждения с последствиями.
 * - Отказ сервера (`400 BASIS_REQUIRED`, `400 VALIDATION`) — его ответ и код ошибки у кнопки; введённое остаётся.
 *   После записи — сообщение, данные страницы — ответ `PUT`, форма — из него.
 * - Один интерфейс на 375/768/1280 px: поля номера и даты, лимиты — в две колонки, на телефоне — в одну; варианты и
 *   флажки — строки не ниже 40 px, кнопки на телефоне — во всю ширину.
 */

type BasisKind = (typeof AGENT_DATA_BASIS_KINDS)[number];

/** Номер документа основания — не длиннее, чем принимает сервер (`AgentDataPut.basisRef`). */
const BASIS_REF_MAX = 200;

/** Введённое в форму: числа — строками, как в полях. */
interface Draft {
  enabled: boolean;
  basisKind: BasisKind | null;
  basisRef: string;
  basisDate: string;
  clients: readonly string[];
  perResponse: string;
  perDay: string;
}

type FieldKey = 'basisKind' | 'basisRef' | 'basisDate' | 'perResponse' | 'perDay';
/** Порядок полей на странице: фокус — в первое поле с ошибкой. */
const ORDER: readonly FieldKey[] = ['basisKind', 'basisRef', 'basisDate', 'perResponse', 'perDay'];
const BASIS_FIELDS: ReadonlySet<FieldKey> = new Set(['basisKind', 'basisRef', 'basisDate']);

const draftOf = (v: AgentDataView): Draft => ({
  enabled: v.enabled,
  basisKind: v.basis?.kind ?? null,
  basisRef: v.basis?.ref ?? '',
  basisDate: v.basis?.date ?? '',
  clients: v.clients.map((c) => c.id),
  perResponse: String(v.limits.perResponse),
  perDay: String(v.limits.perDay),
});

const WHOLE = /^\d+$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Целое число из поля в пределах `1…max`. */
const inRange = (s: string, max: number) => WHOLE.test(s) && Number(s) >= 1 && Number(s) <= max;

/** Ошибки введённого: основание — только при «Включены», лимиты — всегда. */
function problemsOf(d: Draft): Partial<Record<FieldKey, string>> {
  const p: Partial<Record<FieldKey, string>> = {};
  if (d.enabled) {
    if (!d.basisKind) p.basisKind = T.errKind;
    if (d.basisRef.trim() === '') p.basisRef = T.errRef;
    if (!DATE.test(d.basisDate)) p.basisDate = T.errDate;
  }
  if (!inRange(d.perResponse.trim(), SAMPLE_PER_RESPONSE_MAX)) p.perResponse = T.errPerResponse;
  if (!inRange(d.perDay.trim(), SAMPLE_PER_DAY_MAX)) p.perDay = T.errPerDay;
  return p;
}

/** Тело `PUT`: пустое — `null`, клиенты — в порядке списка на странице. */
function putOf(d: Draft, list: readonly AgentDataClient[]): AgentDataPut {
  const ref = d.basisRef.trim();
  return {
    enabled: d.enabled,
    basisKind: d.basisKind,
    basisRef: ref === '' ? null : ref,
    basisDate: d.basisDate === '' ? null : d.basisDate,
    clients: list.map((c) => c.id).filter((id) => d.clients.includes(id)),
    limits: { perResponse: Number(d.perResponse.trim()), perDay: Number(d.perDay.trim()) },
  };
}

const samePut = (a: AgentDataPut, b: AgentDataPut) =>
  a.enabled === b.enabled &&
  (a.basisKind ?? null) === (b.basisKind ?? null) &&
  (a.basisRef ?? null) === (b.basisRef ?? null) &&
  (a.basisDate ?? null) === (b.basisDate ?? null) &&
  a.clients.length === b.clients.length &&
  a.clients.every((id) => b.clients.includes(id)) &&
  a.limits.perResponse === b.limits.perResponse &&
  a.limits.perDay === b.limits.perDay;

/** Клиенты для флажков: белый список сервера и клиенты разрешения, которых в нём уже нет (их можно снять). */
function clientList(data: AgentDataView): AgentDataClient[] {
  return [...data.available, ...data.clients.filter((c) => !data.available.some((a) => a.id === c.id))];
}

const CHECK = 'relative top-[3px] h-4 w-4 shrink-0 accent-accent';
const CHOICE = 'flex cursor-pointer items-start gap-2.5 py-1 text-[13.5px] text-ink max-sm:min-h-10';

export function PermissionForm({
  data,
  onSaved,
}: {
  data: AgentDataView;
  /** Разрешение после записи — ответ `PUT`. */
  onSaved: (view: AgentDataView) => void;
}) {
  const ids = useId();
  const { showInfo } = useToast();
  const list = clientList(data);
  const [draft, setDraft] = useState<Draft>(() => draftOf(data));
  const [submitted, setSubmitted] = useState(false);
  const [confirm, setConfirm] = useState<AgentDataPut | null>(null);
  const refs: Record<FieldKey, RefObject<HTMLInputElement | null>> = {
    basisKind: useRef<HTMLInputElement>(null),
    basisRef: useRef<HTMLInputElement>(null),
    basisDate: useRef<HTMLInputElement>(null),
    perResponse: useRef<HTMLInputElement>(null),
    perDay: useRef<HTMLInputElement>(null),
  };

  const save = useMutation({
    mutationFn: saveAgentData,
    onSuccess: (view, body) => {
      setConfirm(null);
      showInfo(
        !data.enabled && body.enabled ? T.enabled : data.enabled && !body.enabled ? T.disabled : T.saved,
      );
      onSaved(view);
    },
    onError: (e) => {
      setConfirm(null);
      if (shouldReport(e)) reportClientError(SCREEN, 'save permission', e);
    },
  });

  const problems = submitted ? problemsOf(draft) : {};
  const basisMissing = ORDER.some((k) => BASIS_FIELDS.has(k) && problems[k]);
  const dirty = !samePut(putOf(draft, list), putOf(draftOf(data), list));
  const busy = save.isPending;
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setSubmitted(true);
    const p = problemsOf(draft);
    const first = ORDER.find((k) => p[k]);
    if (first) {
      refs[first].current?.focus();
      return;
    }
    const body = putOf(draft, list);
    save.reset();
    // Выключение отзывает ссылки выборок — сначала подтверждение.
    if (data.enabled && !body.enabled) setConfirm(body);
    else save.mutate(body);
  };

  const reset = () => {
    setDraft(draftOf(data));
    setSubmitted(false);
    save.reset();
  };

  return (
    <Card>
      <form aria-label={TITLE.permission} noValidate onSubmit={submit}>
        <CardHeader title={TITLE.permission} description={T.stateHint} />

        <Section title={TITLE.state}>
          <Choices label={TITLE.state}>
            {([false, true] as const).map((on) => (
              <label key={String(on)} className={`${CHOICE} items-center`}>
                <input
                  type="radio"
                  name={`${ids}-state`}
                  checked={draft.enabled === on}
                  onChange={() => set({ enabled: on })}
                  className="h-4 w-4 shrink-0 accent-accent"
                />
                <span>{on ? LABEL.on : LABEL.off}</span>
              </label>
            ))}
          </Choices>
        </Section>

        <Section title={TITLE.basis} hint={T.basisHint}>
          <div>
            <p id={`${ids}-kind`} className="mb-1 text-[13px] font-medium text-ink">
              {LABEL.basisKind}
            </p>
            <div
              role="radiogroup"
              aria-labelledby={`${ids}-kind`}
              aria-invalid={problems.basisKind ? true : undefined}
              aria-describedby={problems.basisKind ? `${ids}-kind-err` : undefined}
              className="flex flex-wrap gap-x-6 max-sm:flex-col"
            >
              {AGENT_DATA_BASIS_KINDS.map((k, i) => (
                <label key={k} className={`${CHOICE} items-center`}>
                  <input
                    ref={i === 0 ? refs.basisKind : undefined}
                    type="radio"
                    name={`${ids}-kind`}
                    checked={draft.basisKind === k}
                    onChange={() => set({ basisKind: k })}
                    className="h-4 w-4 shrink-0 accent-accent"
                  />
                  <span>{BASIS_KIND_LABEL[k]}</span>
                </label>
              ))}
            </div>
            {problems.basisKind && (
              <p id={`${ids}-kind-err`} className="mt-1 text-[12px] text-bad">
                {problems.basisKind}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 max-sm:grid-cols-1">
            <TextField
              id={`${ids}-ref`}
              label={LABEL.basisRef}
              inputRef={refs.basisRef}
              value={draft.basisRef}
              error={problems.basisRef}
              onChange={(v) => set({ basisRef: v })}
              maxLength={BASIS_REF_MAX}
            />
            <TextField
              id={`${ids}-date`}
              label={LABEL.basisDate}
              type="date"
              inputRef={refs.basisDate}
              value={draft.basisDate}
              error={problems.basisDate}
              onChange={(v) => set({ basisDate: v })}
            />
          </div>
        </Section>

        <Section title={TITLE.clients} hint={T.clientsHint} group>
          {list.length === 0 ? (
            <p className="text-[13px] text-ink-3">{T.noKnownClients}</p>
          ) : (
            <ul className="space-y-1">
              {list.map((c) => (
                <li key={c.id}>
                  <label className={CHOICE}>
                    <input
                      type="checkbox"
                      checked={draft.clients.includes(c.id)}
                      onChange={(e) =>
                        set({
                          clients: e.target.checked
                            ? [...draft.clients, c.id]
                            : draft.clients.filter((id) => id !== c.id),
                        })
                      }
                      className={CHECK}
                    />
                    <span className="min-w-0">
                      <span className="block font-medium">{c.name}</span>
                      <span className="block font-mono text-[12px] break-all text-ink-3">{c.host}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={TITLE.limits} hint={T.limitsHint}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 max-sm:grid-cols-1">
            <TextField
              id={`${ids}-per-response`}
              label={LABEL.perResponse}
              inputMode="numeric"
              inputRef={refs.perResponse}
              value={draft.perResponse}
              error={problems.perResponse}
              onChange={(v) => set({ perResponse: v })}
              numeric
            />
            <TextField
              id={`${ids}-per-day`}
              label={LABEL.perDay}
              inputMode="numeric"
              inputRef={refs.perDay}
              value={draft.perDay}
              error={problems.perDay}
              onChange={(v) => set({ perDay: v })}
              numeric
            />
          </div>
        </Section>

        <footer className="space-y-3 border-t border-line px-6 py-4 max-sm:px-4">
          {basisMissing && (
            <p role="alert" className="text-[13px] text-bad">
              {T.basisRequired}
            </p>
          )}
          {save.error && <SaveError error={save.error} />}
          <div className="flex flex-wrap items-center justify-end gap-2 max-sm:[&>button]:flex-auto">
            <Button variant="ghost" disabled={!dirty || busy} onClick={reset}>
              {LABEL.reset}
            </Button>
            <Button type="submit" variant="primary" disabled={!dirty || busy}>
              {busy ? LABEL.saving : LABEL.save}
            </Button>
          </div>
        </footer>
      </form>

      <AlertDialog
        open={confirm !== null}
        // Пока идёт запись, окно не закрывается: итог покажет сообщение.
        onClose={() => {
          if (!busy) setConfirm(null);
        }}
        title={TITLE.disableConfirm}
        icon={ShieldOff}
        tone="bad"
        description={T.disableText}
      >
        <Button data-autofocus disabled={busy} onClick={() => setConfirm(null)}>
          {LABEL.cancel}
        </Button>
        <Button variant="danger" disabled={busy} onClick={() => confirm && save.mutate(confirm)}>
          {busy ? LABEL.saving : LABEL.disable}
        </Button>
      </AlertDialog>
    </Card>
  );
}

/** Раздел формы: заголовок и пояснение; `group` — флажки одной группы (имя группы — заголовок). */
function Section({
  title,
  hint,
  group,
  children,
}: { title: string; hint?: string; group?: boolean; children: ReactNode }) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      role={group ? 'group' : undefined}
      className="space-y-3 border-t border-line px-6 py-5 max-sm:px-4"
    >
      <div>
        <h3 id={id} className="text-[14px] font-semibold text-ink">
          {title}
        </h3>
        {hint && <p className="mt-0.5 text-[13px] text-ink-2">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/** Варианты одного выбора в строку; на телефоне — столбцом. */
function Choices({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-x-6 max-sm:flex-col">
      {children}
    </div>
  );
}

/** Поле с подписью сверху и ошибкой под ним (ошибка — одной строкой, раскладка не прыгает). */
function TextField({
  id,
  label,
  value,
  error,
  onChange,
  inputRef,
  type = 'text',
  inputMode,
  maxLength,
  numeric,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (v: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  type?: 'text' | 'date';
  inputMode?: 'numeric';
  maxLength?: number;
  numeric?: boolean;
}) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-ink">
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        autoComplete="off"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} aria-invalid:border-bad ${numeric ? 'tabular-nums' : ''}`}
      />
      <p id={`${id}-err`} className="mt-1 min-h-[18px] text-[12px] text-bad">
        {error ?? ''}
      </p>
    </div>
  );
}

/** Отказ сервера у кнопки: что не сохранено, ответ сервера и код ошибки. Введённое остаётся. */
function SaveError({ error }: { error: Error }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const text = errorText(error);
  const code = errorCode(error);
  // Ответ сервера словами — если он добавляет подробности к тексту кода.
  const server = error instanceof ApiError && error.message && error.message !== text ? error.message : null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-control bg-bad-soft px-3 py-2.5 text-[13px] text-bad"
    >
      <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium">{T.saveFailed}.</p>
        <p>{errorSentence(error)}</p>
        {server && <p>{server}</p>}
        {(requestId || code) && (
          <p className="mt-0.5 text-[12px]">
            {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
          </p>
        )}
      </div>
    </div>
  );
}
